import { useEffect, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { canShowFomoNudge } from '../utils/canShowFomoNudge';

/**
 * React hook that determines whether the FOMO nudge is allowed to be shown
 * based on the user's Functional cookie consent preferences.
 *
 * The hook reads the `rovo_fomo_nudge_cookie_policy_check` feature gate
 * internally — consumers (Confluence / Jira) do not need to check the gate
 * themselves.
 *
 * Returns:
 *  - `true` when:
 *      - the cookie policy gate is OFF (legacy behavior), OR
 *      - the user has consented to Functional storage preferences
 *  - `false` when the cookie policy gate is ON and consent is denied or
 *    unavailable
 *  - `null` while the consent check is in flight (only possible when the gate
 *    is ON); consumers should hide the nudge in this state
 *
 * Consumers should treat anything other than `true` as a signal to hide the
 * nudge entirely (do not register it with the AI Smart Button store, do not
 * fetch any TAP traits or collaborator data).
 */
export function useCanShowFomoNudge(): boolean | null {
	const cookieConsentEnforced = fg('rovo_fomo_nudge_cookie_policy_check');

	// When the gate is off, short-circuit: legacy behavior, always allow.
	const [canShow, setCanShow] = useState<boolean | null>(cookieConsentEnforced ? null : true);

	useEffect(() => {
		if (!cookieConsentEnforced) {
			setCanShow(true);
			return;
		}

		let isMounted = true;
		setCanShow(null);

		void canShowFomoNudge().then((result) => {
			if (isMounted) {
				setCanShow(result);
			}
		});

		return () => {
			isMounted = false;
		};
	}, [cookieConsentEnforced]);

	return canShow;
}
